import React from 'react';
import DOMPurify from 'dompurify';
import { Loader2, FileText, Copy, Download, ChevronDown, FileText as FileTextIcon, FileDown, Terminal, History, X, Maximize2 } from 'lucide-react';
import { Interpretation } from '../types';
import { renderMarkdownToHtml } from '../utils/helpers';
import { InterpretationSections } from './InterpretationSections';

interface ResultsDisplayProps {
  isStreaming: boolean;
  rawStreamText: string;
  streamingText: string;
  streamContainerRef: React.RefObject<HTMLDivElement>;
  interpretation: Interpretation | null;
  showRawResponse: boolean;
  setShowRawResponse: (show: boolean) => void;
  savedRawText: string;
  thinkingProcess: string;
  showExportDropdown: boolean;
  setShowExportDropdown: (show: boolean) => void;
  onDownloadReport: (format: 'txt' | 'docx' | 'markdown' | 'pdf') => void;
  onCopySection: (title: string, content: string) => void;
  /** Partial text recovered after an interrupted (reloaded) generation. */
  recoveredPartialText?: string | null;
  /** Inserts the recovered text into the raw preview (parent syncs raw state). */
  onInsertRecoveredPartial?: () => void;
  /** Discards the recovered text and clears its sessionStorage key. */
  onDiscardRecoveredPartial?: () => void;
  /**
   * Otwiera pełnoekranowy widok wyniku (działa też podczas streamingu).
   * Dostaje prostokąt panelu wyniku jako origin animacji FLIP — overlay
   * WYRASTA z tego miejsca do pełnego ekranu.
   */
  onOpenFullscreen?: (originRect: DOMRect | null) => void;
  /** Kopiuje wszystkie niepuste sekcje jako czysty tekst. */
  onCopyAll?: () => void;
  /** Diagnoza była ręcznie redagowana (edytor całości) — badge „edytowano". */
  diagnosisEdited?: boolean;
  /** Model, który faktycznie wygenerował ostatnią interpretację. */
  answeredModel?: string | null;
  /** Komunikat, gdy odpowiedział model zapasowy (łańcuch fallback Gemini). */
  generationNotice?: string | null;
}

export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({
  isStreaming,
  rawStreamText,
  streamingText,
  streamContainerRef,
  interpretation,
  showRawResponse,
  setShowRawResponse,
  savedRawText,
  thinkingProcess,
  showExportDropdown,
  setShowExportDropdown,
  onDownloadReport,
  onCopySection,
  recoveredPartialText,
  onInsertRecoveredPartial,
  onDiscardRecoveredPartial,
  onOpenFullscreen,
  onCopyAll,
  diagnosisEdited = false,
  answeredModel = null,
  generationNotice = null
}) => {
  // Whether the recovered fragment is expanded in the local raw preview.
  // Rendered locally so the fragment is visible even when no interpretation
  // exists yet (the main raw section only renders alongside an interpretation).
  const [recoveredExpanded, setRecoveredExpanded] = React.useState(false);

  // Auto-scroll to the results panel when a new interpretation is ready
  // (transition: generating -> done, or interpretation appears/changes).
  const panelRef = React.useRef<HTMLDivElement>(null);
  const prevInterpretationRef = React.useRef<Interpretation | null>(null);
  const mountedAtRef = React.useRef(Date.now());

  React.useEffect(() => {
    const isReady = Boolean(interpretation) && !isStreaming;
    // An interpretation appearing right after mount comes from the
    // sessionStorage restore, not from a generation — don't yank the page
    // down on reload. A real generation always takes seconds.
    const isRestoreOnLoad = Date.now() - mountedAtRef.current < 1500;
    if (isReady && interpretation !== prevInterpretationRef.current && !isRestoreOnLoad) {
      panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
    if (!isStreaming) {
      prevInterpretationRef.current = interpretation;
    }
  }, [interpretation, isStreaming]);

  // Ikona akcji panelu (pełny ekran) — wspólna dla nagłówka streamingu i wyniku.
  // Origin animacji FLIP = prostokąt panelu wyniku (overlay z niego wyrasta).
  const handleOpenFullscreen = () => {
    onOpenFullscreen?.(panelRef.current?.getBoundingClientRect() ?? null);
  };
  const panelActionButtons = onOpenFullscreen ? (
    <button
      onClick={handleOpenFullscreen}
      title="Pełny ekran wyniku"
      aria-label="Pełny ekran wyniku"
      className="p-1.5 bg-surface-card border border-brd text-txt-secondary hover:text-txt-primary hover:bg-surface-subtle rounded-lg transition-colors duration-200 shadow-sm"
    >
      <Maximize2 className="w-3.5 h-3.5" />
    </button>
  ) : null;

  return (
    <div ref={panelRef} className="flex flex-col gap-5 scroll-mt-28">
      {/* Recovered partial generation banner */}
      {recoveredPartialText && !isStreaming && (
        <div className="glass-pane p-4 rounded-2xl border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/40 shadow-sm animate-fadeIn flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <History className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                Odzyskano fragment przerwanej generacji
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => {
                  setRecoveredExpanded(true);
                  onInsertRecoveredPartial?.();
                }}
                className="px-2.5 py-1 bg-surface-card hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-700 rounded-lg text-[10px] font-bold transition-all duration-200"
              >
                Wstaw do podglądu
              </button>
              <button
                onClick={() => {
                  setRecoveredExpanded(false);
                  onDiscardRecoveredPartial?.();
                }}
                className="px-2.5 py-1 bg-surface-card hover:bg-surface-subtle text-txt-secondary border border-brd rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all duration-200"
              >
                <X className="w-3 h-3" />
                Odrzuć
              </button>
            </div>
          </div>
          {recoveredExpanded && (
            <div
              className="max-h-[300px] overflow-y-auto p-3 bg-surface-card border border-amber-200/60 dark:border-amber-800/60 rounded-xl text-xs leading-relaxed prose dark:prose-invert select-text"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdownToHtml(recoveredPartialText)) }}
            />
          )}
        </div>
      )}

      {/* Streaming Display — bez animate-fadeIn (5.3c): blok warunkowy montowany
          w trakcie generacji nie odpala animacji wejścia (anty-migotanie W3). */}
      {isStreaming && (rawStreamText || streamingText) && (
        <div className="glass-pane p-5 rounded-2xl border-accent-border relative overflow-hidden card-streaming-active">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-txt-primary flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-accent animate-spin" />
              Generowanie Raportu Diagnostycznego...
            </h2>
            <div className="flex items-center gap-2">
              <div className="text-[10px] text-txt-tertiary font-mono">
                {Math.ceil(streamingText.length / 4)} tokenów
              </div>
              {panelActionButtons}
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-surface-subtle rounded-full overflow-hidden mb-4 border border-brd">
            <div
              className="h-full bg-accent transition-all duration-300"
              style={{
                width: `${Math.min(100, (streamingText.length / (rawStreamText.length || 1)) * 100)}%`
              }}
            />
          </div>

          <div
            ref={streamContainerRef}
            className="max-h-[350px] min-h-[180px] overflow-y-auto p-4 bg-surface-card border border-accent-border rounded-xl text-xs leading-relaxed text-txt-primary shadow-inner"
          >
            {/* Kursor streamu jako OSOBNY sibling (5.3a): __html zawiera tylko
                treść — przeglądarka diffuje rosnący tekst zamiast odtwarzać cały
                DOM przez doklejony do stringa kursor (anty-migotanie W3). */}
            <div
              className="prose dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdownToHtml(streamingText)) }}
            />
            <span aria-hidden="true" className="inline-block w-1.5 h-3 bg-accent ml-1 animate-pulse" />
          </div>
        </div>
      )}

      {/* Results Display — bez animate-fadeIn (5.3c): przejście stream→wynik
          nie błyska kartą (kaskadę wejścia mają sekcje ai-section-*). */}
      {interpretation && !isStreaming && (
        <div className="glass-pane glass-pane-read p-5 rounded-2xl text-txt-primary flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-base font-bold text-txt-primary flex items-center gap-2 min-w-0">
              <FileText className="w-5 h-5 text-accent-strong shrink-0" />
              <span className="truncate">Wynik Diagnozy Klinicznej</span>
              {diagnosisEdited && (
                <span className="text-[10px] font-medium text-txt-tertiary border border-brd rounded-full px-2 py-0.5 shrink-0">
                  edytowano
                </span>
              )}
            </h2>
            {/* min-w-0 zamiast shrink-0: przyciski łamią się w karcie (shrink-0 wymuszał szerokość
                max-content i na telefonie ikona pełnego ekranu wychodziła poza kartę) */}
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              {onCopyAll && (
                <button
                  onClick={onCopyAll}
                  title="Kopiuj całość raportu jako czysty tekst"
                  className="px-3 py-1.5 bg-surface-card border border-brd text-txt-secondary hover:text-txt-primary hover:bg-surface-subtle rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all duration-200"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Kopiuj całość
                </button>
              )}
              {(savedRawText || rawStreamText) && (
                <button
                  onClick={() => setShowRawResponse(!showRawResponse)}
                  className={`px-3 py-1.5 border rounded-xl text-xs font-semibold transition-colors duration-200 ${
                    showRawResponse
                      ? 'bg-accent-strong border-accent text-white dark:text-emerald-950 shadow-sm'
                      : 'bg-accent-soft border border-accent-border text-accent-strong hover:bg-accent-border'
                  }`}
                >
                  {showRawResponse ? 'Pokaż sekcje' : 'Pełna odpowiedź Markdown'}
                </button>
              )}
              {panelActionButtons}
            </div>
          </div>

          {/* Który model odpowiedział — ostrzeżenie, gdy zadziałał model zapasowy */}
          {generationNotice ? (
            <div
              role="status"
              data-testid="generation-notice"
              className="px-3 py-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/40 text-[11px] font-semibold text-amber-900 dark:text-amber-200"
            >
              ⚠️ {generationNotice}
            </div>
          ) : answeredModel ? (
            <div data-testid="answered-model" className="text-[10px] text-txt-tertiary font-mono">
              Model: {answeredModel}
            </div>
          ) : null}

          {/* Raw response view */}
          {showRawResponse && (savedRawText || rawStreamText) && (
            <div
              className="p-4 bg-surface-subtle border border-brd rounded-xl text-xs leading-relaxed prose dark:prose-invert select-text max-h-[500px] overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdownToHtml(savedRawText || rawStreamText)) }}
            />
          )}

          {/* Sectioned view — wspólny renderer (InterpretationSections) używany też
              przez FullscreenResultDialog */}
          {!showRawResponse && (
            <InterpretationSections
              interpretation={interpretation}
              onCopySection={onCopySection}
              size="compact"
              className="max-h-[500px] overflow-y-auto pr-1"
            />
          )}


          {/* Thinking Process Display as premium developer log */}
          {thinkingProcess && (
            <div className="border border-brd bg-surface-subtle rounded-xl overflow-hidden shadow-sm mt-1.5">
              <div className="flex items-center justify-between bg-surface-subtle px-3.5 py-2 border-b border-brd">
                <div className="flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-accent-strong" />
                  <span className="text-[10px] font-mono font-semibold text-txt-secondary tracking-wider">PROCES_ROZUMOWANIA.log</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-brd" />
                  <span className="w-2.5 h-2.5 rounded-full bg-brd-strong" />
                  <span className="w-2.5 h-2.5 rounded-full bg-txt-tertiary" />
                </div>
              </div>
              <details className="group">
                <summary className="px-3.5 py-2 cursor-pointer text-[10px] font-mono text-txt-secondary hover:text-txt-primary bg-surface-subtle transition-colors select-none">
                  Pokaż log procesu myślenia (rozumowanie kliniczne)...
                </summary>
                <div className="p-3 bg-surface-card border-t border-brd max-h-[220px] overflow-y-auto">
                  <pre className="font-mono text-[9px] text-txt-secondary leading-relaxed whitespace-pre-wrap word-break">
                    {thinkingProcess}
                  </pre>
                </div>
              </details>
            </div>
          )}

          {/* Download Button */}
          <div className="relative mt-2">
            <button
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              className="w-full py-3 bg-accent-strong hover:bg-accent text-white dark:text-emerald-950 font-semibold text-sm rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors duration-200"
            >
              <Download className="w-4 h-4" />
              Pobierz Raport Diagnostyczny
              <ChevronDown className="w-4 h-4" />
            </button>

            {showExportDropdown && (
              /* Nieprzezroczyste tanie tło (bez backdrop-filter) — dropdown
                 wewnątrz kontenera z overflow/scroll (I-1). */
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-surface-card border border-brd shadow-[var(--shadow-float)] rounded-xl overflow-hidden z-50 animate-fadeIn">
                <button
                  onClick={() => onDownloadReport('txt')}
                  className="w-full px-4 py-3 hover:bg-surface-subtle text-txt-secondary hover:text-txt-primary font-semibold text-xs flex items-center gap-2 transition-colors border-b border-brd text-left"
                >
                  <FileTextIcon className="w-4 h-4 text-txt-tertiary" />
                  Eksportuj do pliku TXT (Zwykły tekst)
                </button>

                <button
                  onClick={() => onDownloadReport('docx')}
                  className="w-full px-4 py-3 hover:bg-surface-subtle text-txt-secondary hover:text-txt-primary font-semibold text-xs flex items-center gap-2 transition-colors border-b border-brd text-left"
                >
                  <FileDown className="w-4 h-4 text-blue-500" />
                  Eksportuj do pliku DOCX (Microsoft Word)
                </button>

                <button
                  onClick={() => onDownloadReport('markdown')}
                  className="w-full px-4 py-3 hover:bg-surface-subtle text-txt-secondary hover:text-txt-primary font-semibold text-xs flex items-center gap-2 transition-colors border-b border-brd text-left"
                >
                  <FileText className="w-4 h-4 text-accent" />
                  Eksportuj do pliku MD (Markdown)
                </button>

                <button
                  onClick={() => onDownloadReport('pdf')}
                  className="w-full px-4 py-3 hover:bg-surface-subtle text-txt-secondary hover:text-txt-primary font-semibold text-xs flex items-center gap-2 transition-colors text-left"
                >
                  <FileText className="w-4 h-4 text-red-500" />
                  Eksportuj do pliku PDF (Format wektorowy)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
